import * as styles from "./AppealPage.module.scss";

import { Head as DefaultHead } from "@dogstrust/dt-web-core-lib-front-end/src/components/Head/Head";
import CurvedBorderShallow from "@dogstrust/dt-web-core-lib-front-end/src/components/borders/CurvedBorderShallow";
import { Button } from "@dogstrust/dt-web-core-lib-front-end/src/components/common";
import SEO from "@dogstrust/dt-web-core-lib-front-end/src/components/seo";
import IntroAppealPageImageWithDonate from "@dogstrust/dt-web-core-lib-front-end/src/containers/introBlocks/IntroAppealPageImageWithDonate/IntroAppealPageImageWithDonate";
import { ParagraphSections } from "@dogstrust/dt-web-core-lib-front-end/src/containers/paragraphs/ParagraphSections/ParagraphSections";
import { usePageManager } from "@dogstrust/dt-web-core-lib-front-end/src/context";

import { Colours } from "@dogstrust/dt-web-core-lib-front-end/src/utils/colours";
import { getImagePath } from "@dogstrust/dt-web-core-lib-front-end/src/utils/images";
import { scrollToElement } from "@dogstrust/dt-web-core-lib-front-end/src/utils/scripts";
import { getSeoProps } from "@dogstrust/dt-web-core-lib-front-end/src/utils/seo";
import { MinimalLayout } from "@dogstrust/uk/components/Layout";

import { graphql } from "gatsby";
import React, { useEffect } from "react";

export const Head: React.FC<Props> = ({
	data: {
		nodeAppeal: {
			title,
			path,
			field_hreflang_url,
			field_intro_text,
			field_meta_tags,
			relationships: { field_featured_image },
		},
	},
}) => {
	const description = field_intro_text?.processed || field_intro_text?.value;
	const maxLength = 150; // maximum number of characters to extract
	let trimmedMetaDescription = "";
	if (description) {
		const metaDescription = description.replace(/(<([^>]+)>)/gi, "");
		//trim the description to the maximum length
		trimmedMetaDescription = metaDescription.substr(0, maxLength);
		//re-trim if we are in the middle of a word
		if (metaDescription.length > maxLength) {
			trimmedMetaDescription = trimmedMetaDescription.substr(
				0,
				Math.min(
					trimmedMetaDescription.length,
					trimmedMetaDescription.lastIndexOf(" "),
				),
			);
		}
	}
	return (
		<DefaultHead pageUrl={path.alias} hrefLangUrl={field_hreflang_url}>
			<SEO
				{...getSeoProps(field_meta_tags, {
					title: `${title} | Dogs Trust`,
					description: trimmedMetaDescription,
					canonical_url: process.env.GATSBY_DOMAIN_NAME + path.alias,
					og_type: "article",
					og_url: process.env.GATSBY_DOMAIN_NAME + path.alias,
					og_title: `${title} | Dogs Trust`,
					og_description: trimmedMetaDescription,
					og_image_url: `${getImagePath(
						"400x300",
						field_featured_image?.field_media_image?.uri,
					)}`,
					twitter_cards_type: "summary_large_image",
					twitter_cards_description: trimmedMetaDescription,
					twitter_cards_site: "@DogsTrust",
					twitter_cards_title: `${title} | Dogs Trust`,
					twitter_cards_page_url: process.env.GATSBY_DOMAIN_NAME + path.alias,
					twitter_cards_image: `${getImagePath(
						"400x300",
						field_featured_image?.field_media_image?.uri,
					)}`,
				})}
			/>
		</DefaultHead>
	);
};
interface Props {
	data: Queries.AppealPageQuery;
}

const AppealPage: React.FC<Props> = ({
	data: {
		nodeAppeal: {
			title,
			field_embed_code,
			field_widget_theme,
			field_intro_text,
			field_show_donate_cta,
			field_hide_image_in_intro_header,
			relationships: { field_page_sections, field_featured_image },
		},
	},
}) => {
	const { usePagination, setWithNoBreaks } = usePageManager();

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		usePagination(false);
		setWithNoBreaks(true);

		return () => {
			setWithNoBreaks(false);
		};
	}, []);

	return (
		<MinimalLayout>
			<IntroAppealPageImageWithDonate
				title={title}
				text={field_intro_text.value}
				imageSrc={field_featured_image?.field_media_image.uri}
				imageAlt={field_featured_image?.field_media_image.alt}
				imageInHeader={field_hide_image_in_intro_header}
				widgetTheme={field_widget_theme}
				embedCode={field_embed_code as string}
				isRTE={true}
			/>
			{!field_embed_code && (
				<div className={styles.bordersection}>
					<CurvedBorderShallow
						className={styles.border}
						fill={Colours.WHITE}
						rotation={0}
					/>
				</div>
			)}
			<div className={styles.contentarea}>
				{field_page_sections?.map((section, i) => (
					<ParagraphSections
						key={"id" in section ? section.id : i}
						data={section}
						thisSectionKey={i}
					/>
				))}
				{field_show_donate_cta && (
					<div className={styles.ctacontainer}>
						<Button
							onClick={() => scrollToElement("support-script")}
							variant="secondary"
						>
							Donate now
						</Button>
					</div>
				)}
			</div>
		</MinimalLayout>
	);
};

export default AppealPage;

export const query = graphql`
    query AppealPage($path: String) {
        nodeAppeal(path: { alias: { eq: $path } }) {
            ...Appeal
        }
    }
`;
export const fragment = graphql`
    fragment Appeal on node__appeal {
        path {
            alias
        }
        title
        breadcrumbs {
            ...BreadCrumb
        }
		field_hreflang_url
        field_embed_code
        field_widget_theme
        field_intro_text {
            processed
            value
        }
        field_show_donate_cta
        field_meta_tags {
            ...MetaTags
        }
        field_hide_image_in_intro_header
        relationships {
            field_category_type {
                name
            }
            field_page_sections {
                ...ParagraphSectionsData
            }
            field_featured_image {
                field_media_image {
                    uri
                    alt
                }
            }
        }
    }
`;
